import { lazy, Suspense } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth-context';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/Login';
import { TicketListPage } from './pages/TicketList';
import { NewTicketPage } from './pages/NewTicket';
import { TicketDetailPage } from './pages/TicketDetail';
import { DistributionListsAdminPage } from './pages/admin/DistributionListsPage';
import { UsersAdminPage } from './pages/admin/UsersPage';
import { AffectedAreasAdminPage } from './pages/admin/AffectedAreasPage';
import { DeployReviewersAdminPage } from './pages/admin/DeployReviewersPage';
import { PriorityQueuePage } from './pages/PriorityQueuePage';
import { SettingsPage } from './pages/SettingsPage';
import { SettingsLayout } from './pages/settings/SettingsLayout';
import { SuggestionsPage } from './pages/SuggestionsPage';
import { DeploymentsPage } from './pages/DeploymentsPage';

// Reports is lazy-loaded so recharts (large) doesn't bloat the main bundle for
// users who never open it. Vite splits the chunk on its own when an import()
// boundary is present here.
const ReportsPage = lazy(() =>
  import('./pages/ReportsPage').then((m) => ({ default: m.ReportsPage })),
);

function PageLoading() {
  return (
    <div className="text-sm text-slate-500 py-6">Loading…</div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route element={<RequireAuth />}>
          <Route element={<Layout />}>
            <Route index element={<TicketListPage />} />
            <Route path="tickets/new" element={<NewTicketPage />} />
            <Route path="tickets/:id" element={<TicketDetailPage />} />
            <Route path="queue" element={<PriorityQueuePage />} />
            <Route path="deployments" element={<DeploymentsPage />} />
            <Route path="suggestions" element={<SuggestionsPage />} />
            <Route
              path="reports"
              element={
                <Suspense fallback={<PageLoading />}>
                  <ReportsPage />
                </Suspense>
              }
            />
            <Route path="settings" element={<SettingsLayout />}>
              <Route index element={<SettingsPage />} />
              <Route path="users" element={<UsersAdminPage />} />
              <Route path="distribution-lists" element={<DistributionListsAdminPage />} />
              <Route path="affected-areas" element={<AffectedAreasAdminPage />} />
              <Route path="deploy-reviewers" element={<DeployReviewersAdminPage />} />
            </Route>
            {/* Legacy /admin/* paths redirect to their /settings/* homes for bookmarks. */}
            <Route path="admin/users" element={<Navigate to="/settings/users" replace />} />
            <Route path="admin/distribution-lists" element={<Navigate to="/settings/distribution-lists" replace />} />
            <Route path="admin/affected-areas" element={<Navigate to="/settings/affected-areas" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Route>
      </Routes>
    </AuthProvider>
  );
}

function RequireAuth() {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="min-h-full flex items-center justify-center text-sm text-slate-500">Loading…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Outlet />;
}
